import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ColourwayCard from "./ColourwayCard";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { useCart } from "../context/CartContext";
import GripSelector from "./GripSelector";

const FeaturedProduct = ({ product }) => {
  const { addItem } = useCart();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingColour, setPendingColour] = useState(null);
  const [size, setSize] = useState(product?.sizes?.[1] || "UK 6-9");
  const [grip, setGrip] = useState(product?.default_grip || "grid");

  useEffect(() => {
    if (product?.default_grip) setGrip(product.default_grip);
  }, [product?.default_grip]);

  if (!product) return null;

  const handleQuickAdd = (colour) => {
    setPendingColour(colour);
    setSize(product.sizes[1] || product.sizes[0]);
    setGrip(product.default_grip || product.grips?.[0]?.slug || "grid");
    setPickerOpen(true);
  };

  const confirmAdd = () => {
    if (!pendingColour) return;
    const selectedGrip = (product.grips || []).find((g) => g.slug === grip);
    addItem({
      type: "sock",
      slug: product.slug,
      name: product.name,
      colourway: pendingColour.slug,
      colourwayLabel: pendingColour.name,
      swatch: pendingColour.hex,
      size,
      grip,
      gripLabel: selectedGrip?.name,
      price: product.price,
      image: product.image,
    });
    setPickerOpen(false);
  };

  return (
    <section className="border-t border-line" id="colourways" data-testid="featured-product">
      <div className="container-x py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-10 md:mb-14">
          <div className="md:col-span-7">
            <p className="overline mb-4">The Series</p>
            <h2 className="font-display uppercase text-4xl md:text-6xl tracking-tightest font-bold leading-[0.95]">
              The Pritch
              <br />
              <span className="text-mute">Grip Sock.</span>
            </h2>
          </div>
          <div className="md:col-span-5 flex md:items-end">
            <p className="text-mute text-base leading-relaxed max-w-md">
              The first product from Pritch Premium Pitch Wear. A football-specific grip sock built
              for players who want control, comfort and a cleaner matchday look.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-line border border-line">
          {product.colourways.map((c, idx) => (
            <ColourwayCard
              key={c.slug}
              colour={c}
              price={product.price}
              onAdd={handleQuickAdd}
              index={idx}
              totalCount={product.colourways.length}
            />
          ))}
        </div>

        <div className="mt-10 flex items-center justify-between flex-wrap gap-4">
          <p className="text-sm text-mute">12 colourways · UK 3–12 · 8 grip patterns · Built in match-grade Polyamide / Elastane.</p>
          <Link to="/shop" className="btn-secondary" data-testid="featured-view-all">
            View full collection
          </Link>
        </div>
      </div>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent
          className="bg-white border border-line rounded-none max-w-lg p-0"
          data-testid="quick-add-dialog"
        >
          <DialogHeader className="px-6 py-4 border-b border-line">
            <DialogTitle className="font-display uppercase tracking-tight text-xl">
              Quick add · {pendingColour?.name}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Choose your size and grip pattern to add {pendingColour?.name} grip socks to your cart.
            </DialogDescription>
          </DialogHeader>
          <div className="p-6 max-h-[80vh] overflow-y-auto hide-scrollbar">
            <div className="flex items-center gap-4 mb-6">
              <div
                className="w-16 h-16 border border-line"
                style={{ backgroundColor: pendingColour?.hex }}
              />
              <div>
                <p className="text-sm font-medium">{product.name}</p>
                <p className="text-xs text-mute mt-1">{pendingColour?.description}</p>
              </div>
            </div>

            <p className="overline mb-3">Select size</p>
            <div className="grid grid-cols-3 gap-2">
              {product.sizes.map((s) => (
                <button
                  type="button"
                  key={s}
                  onClick={() => setSize(s)}
                  className={`py-3 text-[12px] tracking-[0.16em] uppercase font-bold border transition-colors ${
                    size === s
                      ? "bg-ink text-white border-ink"
                      : "bg-white text-ink border-line hover:border-ink"
                  }`}
                  data-testid={`quick-add-size-${s.replace(/\s+/g, "-")}`}
                >
                  {s}
                </button>
              ))}
            </div>

            <p className="overline mt-6 mb-3">
              Select grip · {(product.grips || []).find((g) => g.slug === grip)?.name || "Pritch Grid"}
            </p>
            <GripSelector
              grips={product.grips || []}
              value={grip}
              onChange={setGrip}
              testIdPrefix="quick-add-grip"
            />

            <button
              type="button"
              onClick={confirmAdd}
              className="btn-primary w-full mt-6"
              data-testid="quick-add-confirm"
            >
              Add to cart · £{product.price.toFixed(2)}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default FeaturedProduct;
